import type {
  CustomBlockDeployedEntity,
  CustomBlockEntity,
} from '@unifyapps/defs/blocks/Custom/types';
import type { DataSourceEntity } from '@unifyapps/defs/types/dataSource';
import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import type { InterfaceNamespaceEntity } from '@unifyapps/defs/types/namespace';
import type { InterfacePageEntity } from '@unifyapps/defs/types/page';
import type React from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * Explicit snapshot of all ancestor-store entities that template components need for
 * resource lookups. Set by InterfacePageProviders (via InterfaceResourcesStoreProvider) before
 * its child InterfaceStore context is created, so nested templates always read from the correct
 * root store regardless of how many InterfacePageProviders are in between.
 */
export interface InterfaceResourcesStoreData {
  interfacePages: Record<string, InterfacePageEntity | undefined>;
  interfaceRecords: Record<string, InterfaceEntity | undefined>;
  interfacePageNamespaces: Record<string, InterfaceNamespaceEntity | undefined>;
  customBlocks: CustomBlockEntity[] | undefined;
  customBlocksSnapshots: CustomBlockDeployedEntity[] | undefined;
  dataSources: Record<string, DataSourceEntity | undefined>;
}

/**
 * Context value exposes both the data snapshot and `addEmbeddedResources` for builder-side
 * effects to inject dynamically fetched resources (e.g., template components added from picker).
 */
export interface InterfaceResourcesStoreContextValue extends InterfaceResourcesStoreData {
  // narrow input to data-only keys — prevents accidentally clobbering the setter itself
  addEmbeddedResources: (resources: Partial<InterfaceResourcesStoreData>) => void;
}

export const InterfaceResourcesStoreContext =
  createContext<InterfaceResourcesStoreContextValue | null>(null);

/**
 * Returns the nearest InterfaceResourcesStoreContextValue set by a parent InterfacePageProviders,
 * or throws when called outside of one.
 */
export function useInterfaceResourcesContext(): InterfaceResourcesStoreContextValue {
  const context = useContext(InterfaceResourcesStoreContext);
  if (!context) {
    throw new Error('InterfaceResourcesStoreContext not found');
  }
  return context;
}

type InterfaceResourcesStoreProviderProps = {
  embeddedPages?: Record<string, InterfacePageEntity | undefined>;
  embeddedInterfaceRecords?: Record<string, InterfaceEntity | undefined>;
  embeddedInterfacePageNamespaces?: Record<string, InterfaceNamespaceEntity | undefined>;
  customBlocks?: CustomBlockEntity[] | undefined;
  customBlocksSnapshots?: CustomBlockDeployedEntity[] | undefined;
  dataSources?: Record<string, DataSourceEntity | undefined>;
};

/**
 * Early-returns children unwrapped when an ancestor has already set the context,
 * preventing redundant re-wrapping on every nesting level.
 *
 * For the root provider, maintains mutable state for dynamically added resources
 * (e.g., template components fetched after page load). Exposes addEmbeddedResources
 * in the context value so builder side-effects can inject fetched resources.
 */
export function InterfaceResourcesStoreProvider({
  children,
  embeddedPages,
  embeddedInterfaceRecords,
  embeddedInterfacePageNamespaces,
  customBlocks,
  customBlocksSnapshots,
  dataSources,
}: React.PropsWithChildren<InterfaceResourcesStoreProviderProps>) {
  const existingContext = useContext(InterfaceResourcesStoreContext);
  const [dynamicResources, setDynamicResources] = useState<Partial<InterfaceResourcesStoreData>>(
    {},
  );

  // Native-only side effect — mirrors embedded entities into the shared
  // `interfaceEntityStore.entityMap` so `useCachedInterfaceResources` can
  // resolve them by id. No-op on web (the platform-split index.ts stub).
  // Reads from the merged dynamic + initial resources so dynamically
  // added embeddeds (template picker) sync too.
  // we do not need this natvie for now as it is used for mostly builder purposes where we are adding resources into the dynamicResources when user clicks on the template picker
  // useSyncEmbeddedToInterfaceStore({
  //   embeddedPages: { ...(embeddedPages ?? {}), ...dynamicResources.interfacePages },
  //   embeddedInterfaceRecords: {
  //     ...(embeddedInterfaceRecords ?? {}),
  //     ...dynamicResources.interfaceRecords,
  //   },
  //   embeddedInterfacePageNamespaces: {
  //     ...(embeddedInterfacePageNamespaces ?? {}),
  //     ...dynamicResources.interfacePageNamespaces,
  //   },
  //   dataSources: { ...(dataSources ?? {}), ...dynamicResources.dataSources },
  // });

  const addEmbeddedResources = useCallback((resources: Partial<InterfaceResourcesStoreData>) => {
    setDynamicResources((prev) => ({
      interfacePages: { ...(prev.interfacePages ?? {}), ...resources.interfacePages },
      interfaceRecords: { ...(prev.interfaceRecords ?? {}), ...resources.interfaceRecords },
      interfacePageNamespaces: {
        ...(prev.interfacePageNamespaces ?? {}),
        ...resources.interfacePageNamespaces,
      },
      dataSources: { ...(prev.dataSources ?? {}), ...resources.dataSources },
      customBlocks: resources.customBlocks ?? prev.customBlocks,
      customBlocksSnapshots: resources.customBlocksSnapshots ?? prev.customBlocksSnapshots,
    }));
  }, []);

  const value: InterfaceResourcesStoreContextValue = useMemo(
    () => ({
      interfacePages: { ...(embeddedPages ?? {}), ...dynamicResources.interfacePages },
      interfaceRecords: {
        ...(embeddedInterfaceRecords ?? {}),
        ...dynamicResources.interfaceRecords,
      },
      interfacePageNamespaces: {
        ...(embeddedInterfacePageNamespaces ?? {}),
        ...dynamicResources.interfacePageNamespaces,
      },
      customBlocks: dynamicResources.customBlocks ?? customBlocks ?? [],
      customBlocksSnapshots: dynamicResources.customBlocksSnapshots ?? customBlocksSnapshots ?? [],
      dataSources: { ...(dataSources ?? {}), ...dynamicResources.dataSources },
      addEmbeddedResources,
    }),
    [
      embeddedPages,
      embeddedInterfaceRecords,
      embeddedInterfacePageNamespaces,
      customBlocks,
      customBlocksSnapshots,
      dataSources,
      dynamicResources,
      addEmbeddedResources,
    ],
  );

  // nested template — ancestor already set the context, skip re-wrap
  if (existingContext) {
    return <>{children}</>;
  }

  return (
    <InterfaceResourcesStoreContext.Provider value={value}>
      {children}
    </InterfaceResourcesStoreContext.Provider>
  );
}
